import { logger } from '@/ui/utilities/instrumentation/logger'
import { useMemo } from 'react'
import {
	documentAtom,
	expandedLayerIdsAtom,
	layerAtomFamily,
	layersAtom,
	layerTreeAtom,
	onCommitAtom,
	selectedLayerIdAtom,
} from './atoms'
import { useCanvasStore } from './canvas-store'
import type { CanvasData } from './data'
import type { Layer, LayerType, LeafLayer } from './data/v1'
import { CONTAINER_TYPES, createLayerDefaults, FLOATABLE_TYPES, type LayerNode } from './data/v1'

function isFloatableLayer(layer: Layer): layer is LeafLayer {
	return 'floating' in layer
}

const ROOT_ONLY_TYPES = new Set<LayerType>(['content', 'backdrop'])

export type AddLayerTarget = { inside: string } | { nextTo: string }

/** A resolved destination: `parentId` null means root level, `siblingId` null means append. */
type Placement = { parentId: string | null; siblingId: string | null }

const AT_ROOT: Placement = { parentId: null, siblingId: null }

/** Collect all container layer IDs from a canvas document. */
export function collectContainerIds(doc: CanvasData): Set<string> {
	const ids = new Set<string>()
	function walk(nodes: LayerNode[]) {
		for (const n of nodes) {
			const layer = doc.layers[n.id]
			if (layer && CONTAINER_TYPES.has(layer.type)) { ids.add(n.id) }
			walk(n.children)
		}
	}
	walk(doc.layerTree)
	return ids
}

// --- Tree helpers ---

function appendChild(nodes: LayerNode[], parentId: string, child: LayerNode): LayerNode[] {
	return nodes.map(node => {
		if (node.id === parentId) {
			return { ...node, children: [...node.children, child] }
		}
		return { ...node, children: appendChild(node.children, parentId, child) }
	})
}

function mapChildren(tree: LayerNode[], parentId: string, fn: (children: LayerNode[]) => LayerNode[]): LayerNode[] {
	return tree.map(node => {
		if (node.id === parentId) {
			return { ...node, children: fn(node.children) }
		}
		return { ...node, children: mapChildren(node.children, parentId, fn) }
	})
}

/** The id of the node whose children contain `targetId`, or null when it sits at root. */
export function findParentId(nodes: LayerNode[], targetId: string): string | null {
	for (const node of nodes) {
		if (node.children.some(child => child.id === targetId)) { return node.id }
		const found = findParentId(node.children, targetId)
		if (found) { return found }
	}
	return null
}

/**
 * Places a node immediately in front of a sibling. `layerTree` order is *reverse* paint order.
 */
function insertInFrontOf(nodes: LayerNode[], siblingId: string, node: LayerNode): LayerNode[] {
	const index = nodes.findIndex(candidate => candidate.id === siblingId)
	if (index === -1) { return [node, ...nodes] }
	return [...nodes.slice(0, index), node, ...nodes.slice(index)]
}

/** Every ancestor of `targetId`, outermost first. Empty when it sits at root or is absent. */
function findAncestorIds(nodes: LayerNode[], targetId: string): string[] {
	for (const node of nodes) {
		if (node.children.some(child => child.id === targetId)) { return [node.id] }

		const deeper = findAncestorIds(node.children, targetId)
		if (deeper.length > 0) { return [node.id, ...deeper] }
	}
	return []
}

function containsNode(nodes: LayerNode[], id: string): boolean {
	for (const node of nodes) {
		if (node.id === id) { return true }
		if (containsNode(node.children, id)) { return true }
	}
	return false
}

function isAncestorOf(tree: LayerNode[], ancestorId: string, descendantId: string): boolean {
	for (const node of tree) {
		if (node.id === ancestorId) { return containsNode(node.children, descendantId) }
		if (isAncestorOf(node.children, ancestorId, descendantId)) { return true }
	}
	return false
}

/** What selecting a layer and pressing "+ Add" implies about where the new layer goes. */
function targetFromSelection(selectedLayer: Layer | undefined): AddLayerTarget | undefined {
	// The backdrop is selectable but sits last at root, so a layer placed beside it would land
	// behind every content layer.
	if (!selectedLayer || selectedLayer.type === 'backdrop') { return undefined }

	// A container can hold the layer; a leaf cannot, so it goes beside the leaf rather than at
	// the end of whatever list the leaf happens to be in.
	return CONTAINER_TYPES.has(selectedLayer.type)
		? { inside: selectedLayer.id }
		: { nextTo: selectedLayer.id }
}

export type AddPlacementContext = {
	newLayerType: LayerType
	tree: LayerNode[]
	layerById: (id: string) => Layer | undefined
	selectedLayer: Layer | undefined
	/** Where the layer belongs regardless of what is selected. */
	target?: AddLayerTarget
}

export function resolveAddPlacement(
	{ newLayerType, tree, layerById, selectedLayer, target }: AddPlacementContext,
): Placement {
	// A root-only type has one legal home, so it overrides both the target and the selection.
	if (ROOT_ONLY_TYPES.has(newLayerType)) { return AT_ROOT }

	const destination = target ?? targetFromSelection(selectedLayer)

	if (destination && 'inside' in destination) {
		return { parentId: destination.inside, siblingId: null }
	}

	if (destination) {
		// A sibling at root means root level, which for a floatable type means floating.
		return { parentId: findParentId(tree, destination.nextTo), siblingId: destination.nextTo }
	}

	// a floatable type can float at root
	if (FLOATABLE_TYPES.has(newLayerType)) { return AT_ROOT }

	// Take the front-most root that is a valid container. This prevents a root leaf
	// (if it existed) from swallowing any child layers unrecoverably.
	// As of writing, all existing layer types are either root-only or floatable, so nothing should reach this.
	const rootContainer = tree.find(node => {
		const rootLayer = layerById(node.id)
		return rootLayer !== undefined && CONTAINER_TYPES.has(rootLayer.type)
	})

	// Log if reached; may happen as we add new layer types.
	if (!rootContainer) {
		logger.error(new Error(`Canvas: no root container to hold a new ${newLayerType} layer`), {
			tags: { area: 'canvas-editor' },
			data: { layerType: newLayerType, rootNodeCount: tree.length },
		})
	}

	return { parentId: rootContainer?.id ?? null, siblingId: null }
}

let nextId = 1
function generateId(): string {
	return `layer-${Date.now()}-${nextId++}`
}

export function useLayerTreeActions() {
	const store = useCanvasStore()

	return useMemo(() => {
		function commit() {
			const onCommit = store.get(onCommitAtom)
			if (onCommit) {
				onCommit(store.get(documentAtom))
			}
		}

		function addLayer(newLayerType: LayerType, target?: AddLayerTarget): string {
			const id = generateId()
			const layer = createLayerDefaults(newLayerType, id)
			const treeNode: LayerNode = { id, children: [] }

			const selectedId = store.get(selectedLayerIdAtom)

			// Resolved before the tree write rather than inside it, because the insertion, the
			// floating decision and the expansion all need to agree on where the layer landed.
			const { parentId, siblingId } = resolveAddPlacement({
				newLayerType,
				target,
				tree: store.get(layerTreeAtom),
				layerById: (layerId) => store.get(layerAtomFamily(layerId)),
				selectedLayer: selectedId ? store.get(layerAtomFamily(selectedId)) : undefined,
			})
			const isRootLevel = parentId === null

			if (isRootLevel && FLOATABLE_TYPES.has(newLayerType)) {
				// Root-level leaf → set floating with centered defaults
				const floatingLayer = {
					...layer,
					floating: true,
					floatAnchor: 'center' as const,
					floatX: { mode: '%' as const, pxValue: 0, percentValue: 50 },
					floatY: { mode: '%' as const, pxValue: 0, percentValue: 50 },
				}
				store.set(layersAtom, (prev) => ({ ...prev, [id]: floatingLayer }))
			} else {
				store.set(layersAtom, (prev) => ({ ...prev, [id]: layer }))
			}

			store.set(layerTreeAtom, (prev) => {
				if (parentId === null) {
					return siblingId ? insertInFrontOf(prev, siblingId, treeNode) : [treeNode, ...prev]
				}

				if (siblingId) {
					return mapChildren(prev, parentId, (children) => insertInFrontOf(children, siblingId, treeNode))
				}

				return appendChild(prev, parentId, treeNode)
			})

			store.set(expandedLayerIdsAtom, (prev) => {
				const next = new Set(prev)

				// automatically expand new container layers
				if (CONTAINER_TYPES.has(newLayerType)) { next.add(id) }

				// expand where it landed, and everything above it, so the selection is visible
				if (parentId) {
					next.add(parentId)
					for (const ancestorId of findAncestorIds(store.get(layerTreeAtom), parentId)) {
						next.add(ancestorId)
					}
				}
				return next
			})

			store.set(selectedLayerIdAtom, id)
			commit()
			return id
		}

		function reorderRoots(sourceId: string, targetId: string, edge: 'top' | 'bottom') {
			store.set(layerTreeAtom, (prev) => {
				const sourceIndex = prev.findIndex(n => n.id === sourceId)
				const targetIndex = prev.findIndex(n => n.id === targetId)
				if (sourceIndex === -1 || targetIndex === -1) { return prev }

				const result = [...prev]
				const [removed] = result.splice(sourceIndex, 1)
				const newTargetIndex = result.findIndex(n => n.id === targetId)
				result.splice(edge === 'top' ? newTargetIndex : newTargetIndex + 1, 0, removed)
				return result
			})
			commit()
		}

		function moveChild(
			sourceId: string,
			sourceParentId: string,
			targetParentId: string,
			insertAt: number | 'append',
		) {
			store.set(layerTreeAtom, (prev) => {
				if (sourceId === targetParentId) { return prev }
				if (sourceParentId !== targetParentId && isAncestorOf(prev, sourceId, targetParentId)) { return prev }

				if (sourceParentId === targetParentId) {
					return mapChildren(prev, sourceParentId, (children) => {
						const sourceIndex = children.findIndex(n => n.id === sourceId)
						if (sourceIndex === -1) { return children }

						const result = [...children]
						const [removed] = result.splice(sourceIndex, 1)
						const idx = insertAt === 'append'
							? result.length
							: (sourceIndex < insertAt ? insertAt - 1 : insertAt)
						result.splice(idx, 0, removed)
						return result
					})
				}

				let removed: LayerNode | null = null
				let result = mapChildren(prev, sourceParentId, (children) => {
					const index = children.findIndex(n => n.id === sourceId)
					if (index === -1) { return children }
					removed = children[index]
					return [...children.slice(0, index), ...children.slice(index + 1)]
				})
				if (!removed) { return prev }

				result = mapChildren(result, targetParentId, (children) => {
					const idx = insertAt === 'append' ? children.length : insertAt
					return [...children.slice(0, idx), removed!, ...children.slice(idx)]
				})
				return result
			})
			commit()
		}

		function duplicateLayer(id: string) {
			const layers = store.get(layersAtom)
			const tree = store.get(layerTreeAtom)

			function findNode(nodes: LayerNode[], targetId: string): LayerNode | null {
				for (const node of nodes) {
					if (node.id === targetId) { return node }
					const found = findNode(node.children, targetId)
					if (found) { return found }
				}
				return null
			}

			const node = findNode(tree, id)
			if (!node) { return }

			// Deep-clone tree node with new IDs, collecting new layers along the way
			const newLayers: Record<string, typeof layers[string]> = {}
			const previouslyExpanded = store.get(expandedLayerIdsAtom)
			const expandedCloneIds: string[] = []
			function cloneNode(n: LayerNode): LayerNode {
				const newId = generateId()
				const layer = layers[n.id]
				if (layer) {
					newLayers[newId] = { ...layer, id: newId }
				}
				// Clones carry fresh IDs, so expansion has to be copied across or a duplicated
				// tree would collapse itself no matter how the original was left.
				if (previouslyExpanded.has(n.id)) { expandedCloneIds.push(newId) }
				return { id: newId, children: n.children.map(cloneNode) }
			}
			const clonedNode = cloneNode(node)

			// Add all cloned layers
			store.set(layersAtom, (prev) => ({ ...prev, ...newLayers }))

			if (expandedCloneIds.length > 0) {
				store.set(expandedLayerIdsAtom, (prev) => {
					const next = new Set(prev)
					for (const cloneId of expandedCloneIds) { next.add(cloneId) }
					return next
				})
			}

			// Insert clone after the original in the tree
			const isRoot = tree.some(n => n.id === id)
			if (isRoot) {
				store.set(layerTreeAtom, (prev) => {
					const idx = prev.findIndex(n => n.id === id)
					return [...prev.slice(0, idx + 1), clonedNode, ...prev.slice(idx + 1)]
				})
			} else {
				// Find parent and insert after original within its children
				const parentId = findParentId(tree, id)
				if (parentId) {
					store.set(layerTreeAtom, (prev) =>
						mapChildren(prev, parentId, (children) => {
							const idx = children.findIndex(n => n.id === id)
							return [...children.slice(0, idx + 1), clonedNode, ...children.slice(idx + 1)]
						}))
				}
			}

			store.set(selectedLayerIdAtom, clonedNode.id)
			commit()
		}

		function deleteLayer(id: string) {
			// Collect all descendant IDs to remove from the layers dict
			function collectIds(nodes: LayerNode[]): string[] {
				return nodes.flatMap(n => [n.id, ...collectIds(n.children)])
			}

			function findNode(nodes: LayerNode[], targetId: string): LayerNode | null {
				for (const node of nodes) {
					if (node.id === targetId) { return node }
					const found = findNode(node.children, targetId)
					if (found) { return found }
				}
				return null
			}

			const tree = store.get(layerTreeAtom)
			const node = findNode(tree, id)
			const idsToRemove = node ? [id, ...collectIds(node.children)] : [id]

			// Remove from tree (root or nested)
			function removeFromTree(nodes: LayerNode[]): LayerNode[] {
				return nodes
					.filter(n => n.id !== id)
					.map(n => ({ ...n, children: removeFromTree(n.children) }))
			}
			store.set(layerTreeAtom, (prev) => removeFromTree(prev))

			// Remove from layers dict
			store.set(layersAtom, (prev) => {
				const next = { ...prev }
				for (const rid of idsToRemove) {
					delete next[rid]
				}
				return next
			})

			// Clear selection if deleted
			const selectedId = store.get(selectedLayerIdAtom)
			if (selectedId && idsToRemove.includes(selectedId)) {
				store.set(selectedLayerIdAtom, null)
			}

			commit()
		}

		function moveToRoot(id: string) {
			const layers = store.get(layersAtom)
			const layer = layers[id]
			if (!layer) { return }

			// Remove from current parent in tree
			function removeFromTree(nodes: LayerNode[]): { result: LayerNode[]; removed: LayerNode | null } {
				for (let i = 0; i < nodes.length; i++) {
					const node = nodes[i]
					if (node.children.some(c => c.id === id)) {
						const removed = node.children.find(c => c.id === id)!
						return {
							result: nodes.map(n =>
								n.id === node.id
									? { ...n, children: n.children.filter(c => c.id !== id) }
									: n
							),
							removed,
						}
					}
					const childResult = removeFromTree(node.children)
					if (childResult.removed) {
						return {
							result: nodes.map(n =>
								n.id === node.id
									? { ...n, children: childResult.result }
									: n
							),
							removed: childResult.removed,
						}
					}
				}
				return { result: nodes, removed: null }
			}

			store.set(layerTreeAtom, (prev) => {
				// Already at root
				if (prev.some(n => n.id === id)) { return prev }

				const { result, removed } = removeFromTree(prev)
				if (!removed) { return prev }

				return [removed, ...result]
			})

			// extra defense to avoid an invalid document state
			if (isFloatableLayer(layer)) {
				store.set(layerAtomFamily(id), (prev) =>
					({
						...prev,
						floating: true,
						floatAnchor: 'center' as const,
						floatX: { mode: '%' as const, pxValue: 0, percentValue: 50 },
						floatY: { mode: '%' as const, pxValue: 0, percentValue: 50 },
					}) as typeof prev)
			}

			commit()
		}

		/**
		 * Unified move: removes node from current position, inserts at targetParentId[index].
		 * Handles root↔root, child↔child, root→child, child→root.
		 * Adjusts index when moving within the same parent.
		 * Updates floating flag when moving leaf to/from root.
		 */
		function moveNode(nodeId: string, targetParentId: string | null, targetIndex: number) {
			store.set(layerTreeAtom, (prev) => {
				// Don't drop onto self
				if (nodeId === targetParentId) { return prev }
				// Don't drop into own descendant
				if (targetParentId && isAncestorOf(prev, nodeId, targetParentId)) { return prev }

				// Find current parent
				let currentParentId: string | null = null
				let currentIndex = -1

				const rootIdx = prev.findIndex(n => n.id === nodeId)
				if (rootIdx !== -1) {
					currentParentId = null
					currentIndex = rootIdx
				} else {
					function findParent(nodes: LayerNode[], parentId: string | null): boolean {
						for (let i = 0; i < nodes.length; i++) {
							if (nodes[i].id === nodeId) {
								currentParentId = parentId
								currentIndex = i
								return true
							}
							if (findParent(nodes[i].children, nodes[i].id)) { return true }
						}
						return false
					}
					findParent(prev, null)
				}

				if (currentIndex === -1) { return prev }

				// Adjust index when moving within same parent
				let adjustedIndex = targetIndex
				if (currentParentId === targetParentId && currentIndex < targetIndex) {
					adjustedIndex--
				}

				// Remove from current position
				let removed: LayerNode | null = null
				let result: LayerNode[]

				if (currentParentId === null) {
					// Remove from root
					removed = prev[currentIndex]
					result = [...prev.slice(0, currentIndex), ...prev.slice(currentIndex + 1)]
				} else {
					// Remove from nested parent
					result = mapChildren(prev, currentParentId, (children) => {
						const idx = children.findIndex(n => n.id === nodeId)
						if (idx === -1) { return children }
						removed = children[idx]
						return [...children.slice(0, idx), ...children.slice(idx + 1)]
					})
				}

				if (!removed) { return prev }

				// Insert at target position
				if (targetParentId === null) {
					// Insert at root
					const idx = Math.max(0, Math.min(adjustedIndex, result.length))
					result = [...result.slice(0, idx), removed, ...result.slice(idx)]
				} else {
					// Insert into parent's children
					result = mapChildren(result, targetParentId, (children) => {
						const idx = Math.max(0, Math.min(adjustedIndex, children.length))
						return [...children.slice(0, idx), removed!, ...children.slice(idx)]
					})
				}

				return result
			})

			// Update floating flag for floatable layers
			const layer = store.get(layerAtomFamily(nodeId))
			if (layer && FLOATABLE_TYPES.has(layer.type) && isFloatableLayer(layer)) {
				if (targetParentId === null && !layer.floating) {
					// Moving to root → set floating
					store.set(layerAtomFamily(nodeId), (prev) =>
						({
							...prev,
							floating: true,
							floatAnchor: 'center' as const,
							floatX: { mode: '%' as const, pxValue: 0, percentValue: 50 },
							floatY: { mode: '%' as const, pxValue: 0, percentValue: 50 },
						}) as typeof prev)
				} else if (targetParentId !== null && layer.floating) {
					// Moving from root into container → unset floating
					store.set(layerAtomFamily(nodeId), (prev) => ({ ...prev, floating: false }) as typeof prev)
				}
			}

			commit()
		}

		function applyTemplate(template: CanvasData) {
			store.set(documentAtom, template)
			store.set(selectedLayerIdAtom, null)
			store.set(expandedLayerIdsAtom, collectContainerIds(template))
			commit()
		}

		return {
			addLayer,
			duplicateLayer,
			deleteLayer,
			reorderRoots,
			moveChild,
			moveToRoot,
			moveNode,
			applyTemplate,
		}
	}, [store])
}
